<script setup lang="ts">
import { buildSignupUrl } from '~/utils/backend-sign-up-url';

const config = useRuntimeConfig();

// Marketing CTA — no signup source (organic create-event signups, per Phase 6b).
const signUpUrl = computed(() => {
  const raw = buildSignupUrl({
    plannerOrigin: config.public.plannerUrl,
    frontendOrigin: config.public.siteUrl || useRequestURL().origin,
    redirectTo: '/events/create',
  });
  if (!raw) return '';
  return useCrossPropertyLink({
    url: raw,
    destination: 'planner',
    campaign: 'organize_cta',
    placementId: 'marketing_home',
  });
});

const features = [
  {
    icon: 'mdi-tag-check',
    color: 'primary',
    title: '$25 listing bond',
    description:
      "Submit your event with a $25 bond. It's returned when your event confirms — if it doesn't hit threshold, you're out twenty-five bucks.",
  },
  {
    icon: 'mdi-tune',
    color: 'secondary',
    title: 'You set the terms',
    description:
      'Pick your ticket price, commitment threshold, dates, and venue. Once fundraising starts, the details lock so everyone can plan.',
  },
  {
    icon: 'mdi-shield-half-full',
    color: 'tertiary',
    title: 'Only confirmed events move forward',
    description:
      "If you don't hit threshold, the event doesn't happen and attendees are refunded. Your only risk is the $25 bond.",
  },
];
</script>

<template>
  <section class="organize-section marketing-section">
    <div class="marketing-section__content">
      <h2 class="text-h4 text-center font-weight-bold mb-4">
        Organize with confidence
      </h2>
      <p
        class="text-body-1 text-center text-medium-emphasis mb-10"
        style="max-width: 640px; margin-inline: auto"
      >
        Post your rally, meetup, or gathering and set the commitment threshold
        it needs to be real. Hit the threshold and the event confirms. Miss it
        and attendees are refunded — no messy cancellations.
      </p>

      <v-row class="mb-10">
        <v-col
          v-for="(feature, i) in features"
          :key="i"
          cols="12"
          sm="6"
          md="4"
        >
          <v-card elevation="1" rounded="lg" class="pa-5 h-100">
            <v-icon :color="feature.color" size="36" class="mb-3">
              {{ feature.icon }}
            </v-icon>
            <h3 class="text-subtitle-1 font-weight-bold mb-2">
              {{ feature.title }}
            </h3>
            <p class="text-body-2 text-medium-emphasis mb-0">
              {{ feature.description }}
            </p>
          </v-card>
        </v-col>
      </v-row>

      <div class="text-center">
        <v-btn :href="signUpUrl" color="primary" size="large" class="mb-3">
          Create an event
        </v-btn>
        <p class="text-body-2 text-medium-emphasis">
          Already have an account?
          <NuxtLink
            to="/login?redirect_to=/events/create"
            class="font-weight-bold"
          >
            Sign in
          </NuxtLink>
        </p>
      </div>
    </div>
  </section>
</template>

<style scoped>
.organize-section {
  background: var(--arvee-surface-warm);
}
</style>
